import {
  Box,
  Container,
  Divider,
  Grid,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import { lightBlue } from "@mui/material/colors";
import React from "react";
import { db } from "../../Environment/firebase";
import { useAuthContext } from "../../Modules/Auth/AuthContext";
import CaseCard, { CaseCardType } from "../../Modules/ViewCase/CaseCard";
import { Timestamp } from "firebase/firestore";
import { MstaFontFamily } from "../../Functions/Style/MstaFont";

interface DataType {
  id: string;
  viewTime: Timestamp;
}

const PAGE_SIZE = 20;

function History() {
  const { user } = useAuthContext();
  const [datas, setDatas] = React.useState<DataType[]>([]);
  const [cases, setCases] = React.useState<{ [date: string]: CaseCardType[] }>(
    {}
  );
  const [loading, setLoading] = React.useState(true);
  const [lastDoc, setLastDoc] = React.useState<Timestamp | null>(null);
  const [hasMore, setHasMore] = React.useState(true);

  React.useEffect(() => {
    if (user?.uid) {
      fetchHistory();
    }
  }, [user?.uid]);

  const fetchHistory = async (startAfter: Timestamp | null = null) => {
    setLoading(true);

    try {
      let query = db
        .collection("users")
        .doc(user?.uid)
        .collection("history")
        .orderBy("viewTime", "desc")
        .limit(PAGE_SIZE);

      if (startAfter) {
        query = query.startAfter(startAfter);
      }

      const querySnapshot = await query.get();
      const newDatas: DataType[] = [];

      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        newDatas.push({ id: doc.id, viewTime: docData.viewTime });
      });

      if (newDatas.length < PAGE_SIZE) {
        setHasMore(false);
      }

      setDatas((prev) => [...prev, ...newDatas]);
      setLastDoc(newDatas[newDatas.length - 1]?.viewTime || null);

      const ids = newDatas.map((item) => item.id);
      const dwhData = await getDWHData(ids);

      const groupedCases: { [date: string]: CaseCardType[] } = {};

      newDatas.forEach((item) => {
        const date = item.viewTime
          .toDate()
          .toISOString()
          .split("T")[0]
          .replace(/-/g, "/");
        if (!groupedCases[date]) {
          groupedCases[date] = [];
        }
        const caseData = dwhData.find((caseItem) => caseItem.id === item.id);
        if (caseData) {
          groupedCases[date].push(caseData);
        }
      });

      setCases((prev) => ({ ...prev, ...groupedCases }));
    } finally {
      setLoading(false);
    }
  };

  const getDWHData = async (ids: string[]) => {
    const dwhData: CaseCardType[] = [];
    for (const id of ids) {
      const doc = await db.collection("dwh").doc(id).get();
      if (doc.exists) {
        const docData = doc.data();
        const formattedData: CaseCardType = {
          id: doc.id,
          title: docData?.title,
          age: docData?.ageSearch,
          sex: docData?.sex,
          patientId: docData?.patientId,
          site: docData?.site,
          modality: docData?.modality,
          date: docData?.date,
          organTags: docData?.organTags,
          keyImages: docData?.KeyImages,
          casenumber: docData?.casenumber,
        };
        dwhData.push(formattedData);
      }
    }
    return dwhData;
  };

  const loadMore = () => {
    if (lastDoc) {
      fetchHistory(lastDoc);
    }
  };

  return (
    <Container sx={{ py: 1, mb: 5, minHeight: "95vh" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          m: 1,
          mt: 3,
          gap: 1.5,
        }}
      >
        <Box sx={{ width: 20, height: 44, bgcolor: lightBlue[400] }} />
        <Typography variant="h4" alignSelf="center" fontFamily={MstaFontFamily}>
          閲覧履歴
        </Typography>
      </Box>
      <Divider
        sx={{
          borderBottomWidth: 1.5,
          borderColor: "black",
          mx: 1,
          mt: 1,
          mb: 4,
        }}
      />
      {loading && datas.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            minHeight: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : Object.keys(cases).length === 0 && !loading ? (
        <div className="table">No rows to show</div>
      ) : (
        Object.keys(cases).map((date) => (
          <Box key={date} sx={{ mb: 0 }}>
            <Typography
              variant="h5"
              sx={{ mt: 4, mb: 1, ml: 1, textAlign: "left" }}
            >
              {date}
            </Typography>
            <Grid container sx={{ px: 1 }}>
              <Grid
                item
                container
                spacing={{ xs: 2, md: 4 }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  maxWidth: "100%",
                }}
              >
                {cases[date].map((caseItem) => (
                  <Grid
                    key={caseItem.id}
                    item
                    xs={12}
                    sm={6}
                    sx={{ maxWidth: "100%" }}
                  >
                    <CaseCard
                      id={caseItem.id}
                      title={caseItem.title}
                      age={caseItem.age}
                      sex={caseItem.sex}
                      patientId={caseItem.patientId}
                      site={caseItem.site}
                      modality={caseItem.modality}
                      date={caseItem.date}
                      organTags={caseItem.organTags}
                      keyImages={caseItem.keyImages}
                      casenumber={caseItem.casenumber}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Box>
        ))
      )}
      {hasMore && !loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Button
            onClick={loadMore}
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            Load More
          </Button>
        </Box>
      )}
      {loading && datas.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      )}
    </Container>
  );
}

export default History;
